import { format } from 'date-fns';
import type { ViewKind } from '@/lib/weekCalendar';
import { CalendarToolbar } from '@/components/calendar/layout/CalendarToolbar';
import { ChevronLeftIcon, ChevronRightIcon, MenuIcon, PanelToggleIcon } from '@/components/calendar/shared/CalendarPageIcons';

type Props = {
  anchorDate: Date;
  view: ViewKind;
  isRailCollapsed: boolean;
  isAgendaCollapsed: boolean;
  visibilitySettingsOpen: boolean;
  onOpenMobileRail: () => void;
  onToggleRail: () => void;
  onShowAgenda: () => void;
  onToday: () => void;
  onStep: (dir: -1 | 1) => void;
  onSetView: (view: ViewKind) => void;
  onRefresh: () => void;
  onToggleVisibilitySettings: () => void;
};

const VIEW_LABEL_MAP: Record<ViewKind, string> = {
  day: 'Day',
  '2day': '2 Day',
  '4day': '4 Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
};

export function CalendarMainHeader({
  anchorDate,
  view,
  isAgendaCollapsed,
  visibilitySettingsOpen,
  onOpenMobileRail,
  onShowAgenda,
  onToday,
  onStep,
  onSetView,
  onRefresh,
  onToggleVisibilitySettings,
}: Props) {
  return (
    <header className="sticky top-0 z-30 border-b border-slate-100 bg-white px-4 py-3.5 text-[#0f172b] dark:border-white/5 dark:bg-card dark:text-slate-100 sm:px-6 lg:px-8 lg:z-40">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between xl:justify-start xl:gap-6">
          <div>
            <div className="mb-2 flex items-center gap-2 lg:hidden">
              <button type="button" onClick={onOpenMobileRail} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-600 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 lg:hidden" aria-label="Open sidebar">
                <MenuIcon />
              </button>
            </div>
            <h1 className="whitespace-nowrap text-xl font-bold tracking-tight text-[#0f172b] dark:text-slate-100 sm:text-2xl">
              {format(anchorDate, 'MMMM yyyy')}
            </h1>
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              {view.charAt(0).toUpperCase() + view.slice(1)} view
            </p>
          </div>

          <div className="flex items-center gap-2 sm:ml-4">
            <button type="button" onClick={onToday} className="rounded-full bg-[#F3F4F8] px-5 py-2 text-sm font-semibold text-[#0f172b] transition-colors hover:bg-[#E8EAF2] active:scale-95 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10">Today</button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => onStep(-1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-600 transition-colors hover:bg-[#E8EAF2] active:scale-95 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10">
                <ChevronLeftIcon />
              </button>
              <button type="button" onClick={() => onStep(1)} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-600 transition-colors hover:bg-[#E8EAF2] active:scale-95 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10">
                <ChevronRightIcon />
              </button>
            </div>
            {isAgendaCollapsed ? (
              <button type="button" onClick={onShowAgenda} className="hidden h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-600 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10 lg:inline-flex" aria-label="Show agenda panel" title="Show agenda panel">
                <PanelToggleIcon collapsed />
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between xl:justify-end xl:gap-4">
          <div className="flex items-center gap-1 overflow-x-auto rounded-full bg-[#F3F4F8] p-1 dark:bg-white/5">
            {(['day', '4day', 'week', 'month', 'year'] as ViewKind[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => onSetView(v)}
                className={[
                  'whitespace-nowrap rounded-full px-3.5 py-2 text-xs font-semibold transition-all sm:px-5',
                  v === view ? 'bg-white text-[#5B47E8] shadow-sm dark:bg-[#1C1F3F] dark:text-[#8B7CFF]' : 'text-slate-500 hover:text-[#0f172b] dark:text-slate-400 dark:hover:text-white',
                ].join(' ')}
              >
                {VIEW_LABEL_MAP[v]}
              </button>
            ))}
          </div>
          <div className="self-start sm:self-auto xl:border-l xl:border-slate-200 xl:pl-4 dark:xl:border-white/10">
            <CalendarToolbar onRefresh={onRefresh} visibilitySettingsOpen={visibilitySettingsOpen} onToggleVisibilitySettings={onToggleVisibilitySettings} />
          </div>
        </div>
      </div>
    </header>
  );
}
